import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Rating,
  Autocomplete,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Collapse
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LanguageIcon from '@mui/icons-material/Language';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MapIcon from '@mui/icons-material/Map';
import ListIcon from '@mui/icons-material/List';
import StarIcon from '@mui/icons-material/Star';
import { useLoadScript, GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../config';

const libraries = ['places'];

async function cachePhoto(placeId, googleUrl) {
  try {
    const res = await fetch('/api/cache-photo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ placeId, url: googleUrl })
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.cachedUrl || null;
  } catch { return null; }
}

const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px'
};

const defaultCenter = {
  lat: 1.3521,
  lng: 103.8198
};

const FoodList = ({ list, onUpdateList }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });
  const [places, setPlaces] = useState(list.items || []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlace, setEditingPlace] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [expandedPlaceId, setExpandedPlaceId] = useState(null);

  // Track if maps is ready (loaded and services initialized)
  const [mapsReady, setMapsReady] = useState(false);

  // Form state
  const [searchQuery, setSearchQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [category, setCategory] = useState('');
  const [remarks, setRemarks] = useState('');
  const [visited, setVisited] = useState(false);
  const [visitedRating, setVisitedRating] = useState(null);
  const [visitedNotes, setVisitedNotes] = useState('');
  const [sessionToken, setSessionToken] = useState(null);
  const [placeDetails, setPlaceDetails] = useState(null);

  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const refreshingPhotos = useRef(new Set());
  const latestPlacesRef = useRef(places);
  useEffect(() => { latestPlacesRef.current = places; }, [places]);

  // Initialize Google Places API (runs in background, doesn't block render)
  useEffect(() => {
    if (isLoaded && window.google?.maps?.places) {
      if (!autocompleteService.current) {
        autocompleteService.current = new window.google.maps.places.AutocompleteService();
      }
      if (!placesService.current) {
        const dummyMap = document.createElement('div');
        placesService.current = new window.google.maps.places.PlacesService(dummyMap);
      }
      if (!sessionToken) {
        setSessionToken(new window.google.maps.places.AutocompleteSessionToken());
      }
      // Mark maps as ready once all services are initialized
      setMapsReady(true);
    }
  }, [isLoaded, sessionToken]);

  // Sync places with list items
  useEffect(() => {
    setPlaces(list.items || []);
  }, [list.items]);

  // Proactively re-cache any places still using Google photo URLs.
  // Runs whenever places changes (Firestore may load after mapsReady fires).
  // refreshingPhotos deduplicates so each place is only processed once.
  useEffect(() => {
    if (!mapsReady || !placesService.current) return;
    places.forEach(place => {
      if (!place.placeId || !place.photoURL || place.photoURL.startsWith('/photos/')) return;
      if (refreshingPhotos.current.has(place.placeId)) return;
      refreshingPhotos.current.add(place.placeId);

      placesService.current.getDetails(
        { placeId: place.placeId, fields: ['photos'] },
        async (details, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
            refreshingPhotos.current.delete(place.placeId);
            return;
          }
          const freshGoogleUrl = details.photos && details.photos.length > 0
            ? details.photos[0].getUrl({ maxWidth: 400, maxHeight: 400 })
            : null;
          if (!freshGoogleUrl) {
            refreshingPhotos.current.delete(place.placeId);
            return;
          }
          const cachedUrl = await cachePhoto(place.placeId, freshGoogleUrl);
          const newPhotoURL = cachedUrl || freshGoogleUrl;

          setPlaces(prev => prev.map(p =>
            p.id === place.id ? { ...p, photoURL: newPhotoURL } : p
          ));
          // Use latestPlacesRef so concurrent updates don't overwrite each other
          onUpdateList(list.id, { items: latestPlacesRef.current.map(p =>
            p.id === place.id ? { ...p, photoURL: newPhotoURL } : p
          )});
          refreshingPhotos.current.delete(place.placeId);
        }
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapsReady, places]);

  const handleSearchChange = (e, value) => {
    setSearchQuery(value);

    if (autocompleteService.current && value.trim().length > 0 && sessionToken) {
      autocompleteService.current.getPlacePredictions(
        {
          input: value,
          location: new window.google.maps.LatLng(1.3521, 103.8198),
          radius: 20000,
          types: ['establishment'],
          sessionToken: sessionToken,
        },
        (preds) => {
          if (preds) {
            setPredictions(preds.map(p => ({
              label: p.description,
              value: p.place_id,
              data: p
            })));
          } else {
            setPredictions([]);
          }
        }
      );
    } else {
      setPredictions([]);
    }
  };

  const handleSelectPrediction = (event, value) => {
    if (!value || !placesService.current || !sessionToken) return;

    setSelectedPrediction(value);

    placesService.current.getDetails(
      {
        placeId: value.value,
        sessionToken: sessionToken,
        // Only request fields we actually use to minimize API costs
        fields: ['name', 'place_id', 'rating', 'website', 'geometry', 'photos']
      },
      async (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const googlePhotoURL = place.photos && place.photos.length > 0
            ? place.photos[0].getUrl({ maxWidth: 400, maxHeight: 400 })
            : '';

          // Show image immediately with Google URL
          const details = {
            name: place.name,
            placeId: place.place_id,
            stars: place.rating || '',
            website: place.website || '',
            location: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            photoURL: googlePhotoURL,
            photoGeneratedAt: Date.now()
          };
          setPlaceDetails(details);

          // Generate new session token for next search
          setSessionToken(new window.google.maps.places.AutocompleteSessionToken());

          // Cache photo in background, then update URL to permanent cached version
          if (googlePhotoURL && place.place_id) {
            const cachedUrl = await cachePhoto(place.place_id, googlePhotoURL);
            if (cachedUrl) {
              setPlaceDetails(prev => prev ? { ...prev, photoURL: cachedUrl } : prev);
            }
          }
        }
      }
    );
  };

  const handleOpenDialog = (place = null) => {
    if (place) {
      setEditingPlace(place);
      setSearchQuery(place.name || '');
      setCategory(place.category || '');
      setRemarks(place.remarks || '');
      setVisited(place.visited || false);
      setVisitedRating(place.visitedRating || null);
      setVisitedNotes(place.visitedNotes || '');
      setPlaceDetails(place);
    } else {
      setEditingPlace(null);
      setSearchQuery('');
      setCategory('');
      setRemarks('');
      setVisited(false);
      setVisitedRating(null);
      setVisitedNotes('');
      setPlaceDetails(null);
      setSelectedPrediction(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlace(null);
    setSearchQuery('');
    setPredictions([]);
    setSelectedPrediction(null);
    setPlaceDetails(null);
    setCategory('');
    setRemarks('');
    setVisited(false);
    setVisitedRating(null);
    setVisitedNotes('');
  };

  const handleSave = async () => {
    if (!placeDetails) return;

    const newPlace = {
      id: editingPlace ? editingPlace.id : Date.now().toString(),
      ...placeDetails,
      category,
      remarks,
      visited,
      visitedRating: visited ? visitedRating : null,
      visitedNotes: visited ? visitedNotes : '',
      createdAt: editingPlace ? editingPlace.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedPlaces;
    if (editingPlace) {
      updatedPlaces = places.map(p => p.id === editingPlace.id ? newPlace : p);
    } else {
      updatedPlaces = [...places, newPlace];
    }

    await onUpdateList(list.id, { items: updatedPlaces });
    handleCloseDialog();
  };

  const handleDelete = async (placeId) => {
    if (window.confirm('Are you sure you want to delete this place?')) {
      const updatedPlaces = places.filter(p => p.id !== placeId);
      await onUpdateList(list.id, { items: updatedPlaces });
    }
    handleCloseMenu();
  };

  const handleToggleVisited = async (place) => {
    const updatedPlaces = places.map(p => {
      if (p.id === place.id) {
        return {
          ...p,
          visited: !p.visited,
          visitedRating: !p.visited ? null : p.visitedRating,
          visitedNotes: !p.visited ? '' : p.visitedNotes
        };
      }
      return p;
    });
    await onUpdateList(list.id, { items: updatedPlaces });
  };

  const handleMenuOpen = (event, placeId) => {
    setAnchorEl(event.currentTarget);
    setSelectedPlaceId(placeId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPlaceId(null);
  };

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'mustgo': return 'error';
      case 'luxury': return 'warning';
      case 'healthy': return 'success';
      default: return 'default';
    }
  };

  const getCategoryLabel = (cat) => {
    switch (cat) {
      case 'mustgo': return 'Must Go';
      case 'luxury': return 'Luxury';
      case 'healthy': return 'Healthy';
      default: return 'Other';
    }
  };

  // Group places by category and visited status
  const groupedPlaces = {
    mustgo: places.filter(p => !p.visited && p.category === 'mustgo'),
    luxury: places.filter(p => !p.visited && p.category === 'luxury'),
    healthy: places.filter(p => !p.visited && p.category === 'healthy'),
    others: places.filter(p => !p.visited && (!p.category || p.category === '')),
    visited: places.filter(p => p.visited)
  };

  const handleExpandPlace = (place) => {
    setExpandedPlaceId(expandedPlaceId === place.id ? null : place.id);
    // If in map view, show the marker popup
    if (viewMode === 'map' && place.location) {
      setSelectedMarker(selectedMarker?.id === place.id ? null : place);
    }
  };

  const handlePhotoError = (place) => {
    if (!place.placeId || !mapsReady || !placesService.current) return;
    if (refreshingPhotos.current.has(place.placeId)) return;
    refreshingPhotos.current.add(place.placeId);

    placesService.current.getDetails(
      { placeId: place.placeId, fields: ['photos'] },
      async (details, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK) {
          refreshingPhotos.current.delete(place.placeId);
          return;
        }
        const freshGoogleUrl = details.photos && details.photos.length > 0
          ? details.photos[0].getUrl({ maxWidth: 400, maxHeight: 400 })
          : null;
        if (!freshGoogleUrl) {
          refreshingPhotos.current.delete(place.placeId);
          return;
        }
        const cachedUrl = await cachePhoto(place.placeId, freshGoogleUrl);
        const newPhotoURL = cachedUrl || freshGoogleUrl;

        // Update local state
        setPlaces(prev => prev.map(p =>
          p.id === place.id ? { ...p, photoURL: newPhotoURL } : p
        ));

        // Update Firestore via onUpdateList (use ref to avoid stale closure)
        onUpdateList(list.id, { items: latestPlacesRef.current.map(p =>
          p.id === place.id ? { ...p, photoURL: newPhotoURL } : p
        )});

        refreshingPhotos.current.delete(place.placeId);
      }
    );
  };

  // Helper to render text with clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: '#90caf9' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  const getMapCenter = () => {
    const placesWithLocation = places.filter(p => p.location);
    if (placesWithLocation.length === 0) return defaultCenter;
    const avgLat = placesWithLocation.reduce((sum, p) => sum + p.location.lat, 0) / placesWithLocation.length;
    const avgLng = placesWithLocation.reduce((sum, p) => sum + p.location.lng, 0) / placesWithLocation.length;
    return { lat: avgLat, lng: avgLng };
  };

  const renderPlaceRow = (place) => (
    <Box
      key={place.id}
      sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        mb: 1,
        overflow: 'hidden',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          borderColor: 'rgba(255, 255, 255, 0.2)'
        }
      }}
    >
      {/* Main Row - Compact */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          gap: 1.5,
          cursor: 'pointer'
        }}
        onClick={() => handleExpandPlace(place)}
      >
        {/* Small thumbnail */}
        <Avatar
          src={place.photoURL}
          variant="rounded"
          slotProps={{ img: { onError: () => handlePhotoError(place) } }}
          sx={{
            width: 48,
            height: 48,
            opacity: place.visited ? 0.6 : 1,
            bgcolor: 'rgba(255, 255, 255, 0.1)'
          }}
        >
          <RestaurantIcon sx={{ fontSize: 24 }} />
        </Avatar>

        {/* Name and info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                opacity: place.visited ? 0.7 : 1,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {place.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            {place.stars && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon sx={{ fontSize: 14, color: '#ffc107' }} />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {place.stars}
                </Typography>
              </Box>
            )}
            {place.remarks && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {renderTextWithLinks(place.remarks)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleVisited(place);
            }}
            sx={{ color: place.visited ? 'success.main' : 'rgba(255, 255, 255, 0.4)' }}
          >
            {place.visited ? <CheckCircleIcon fontSize="small" /> : <RadioButtonUncheckedIcon fontSize="small" />}
          </IconButton>
          {place.website && (
            <IconButton
              size="small"
              component="a"
              href={place.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: 'primary.main' }}
            >
              <LanguageIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e, place.id);
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Expandable details - only show if there's visited rating info */}
      {place.visited && place.visitedRating && (
        <Collapse in={expandedPlaceId === place.id}>
          <Box sx={{ px: 1.5, pb: 1.5, pt: 0 }}>
            <Box sx={{ p: 1, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  Your Rating:
                </Typography>
                <Rating value={place.visitedRating} readOnly size="small" />
              </Box>
              {place.visitedNotes && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mt: 0.5 }}>
                  {place.visitedNotes}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );

  // No longer block render - show list immediately while maps loads in background

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      {/* Header with title, view toggle, and add button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 300 }}>
          {list.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              '& .MuiToggleButton-root': {
                color: 'rgba(255, 255, 255, 0.5)',
                border: 'none',
                px: 1.5,
                '&.Mui-selected': {
                  color: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }
            }}
          >
            <ToggleButton value="list">
              <ListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="map">
              <MapIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      {/* Map View */}
      {viewMode === 'map' && places.length > 0 && !isLoaded && (
        <Box sx={{ mb: 3, p: 4, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Loading map...
          </Typography>
        </Box>
      )}
      {viewMode === 'map' && loadError && (
        <Box sx={{ mb: 3, p: 4, textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2 }}>
          <Typography variant="body1" color="error">
            Error loading Google Maps
          </Typography>
        </Box>
      )}
      {viewMode === 'map' && places.length > 0 && isLoaded && !loadError && (
        <Box sx={{ mb: 3 }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            zoom={12}
            center={getMapCenter()}
            onClick={() => setSelectedMarker(null)}
            options={{
              styles: [
                { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
                { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
                { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] }
              ],
              disableDefaultUI: true,
              zoomControl: true
            }}
          >
            {places.filter(p => p.location).map((place) => (
              <MarkerF
                key={place.id}
                position={place.location}
                onClick={() => setSelectedMarker(place)}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: 8,
                  fillColor: place.visited ? '#4caf50' :
                    place.category === 'mustgo' ? '#f44336' :
                    place.category === 'luxury' ? '#ff9800' :
                    place.category === 'healthy' ? '#4caf50' : '#2196f3',
                  fillOpacity: 1,
                  strokeColor: '#fff',
                  strokeWeight: 2
                }}
              />
            ))}
            {selectedMarker && (
              <InfoWindowF
                position={selectedMarker.location}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div style={{ padding: '4px 8px 4px 4px', minWidth: 120, maxWidth: 160 }}>
                  <div style={{ fontWeight: 600, color: '#333', fontSize: '13px', lineHeight: 1.3, marginBottom: 2 }}>
                    {selectedMarker.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {selectedMarker.stars && (
                      <span style={{ color: '#666', fontSize: '11px' }}>
                        ⭐ {selectedMarker.stars}
                      </span>
                    )}
                    {selectedMarker.category && (
                      <span style={{
                        fontSize: '10px',
                        padding: '1px 6px',
                        borderRadius: 8,
                        backgroundColor: selectedMarker.category === 'mustgo' ? '#f44336' :
                          selectedMarker.category === 'luxury' ? '#ff9800' :
                          selectedMarker.category === 'healthy' ? '#4caf50' : '#9e9e9e',
                        color: 'white'
                      }}>
                        {getCategoryLabel(selectedMarker.category)}
                      </span>
                    )}
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        </Box>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Must Go */}
          {groupedPlaces.mustgo.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <RestaurantIcon fontSize="small" /> Must Go ({groupedPlaces.mustgo.length})
              </Typography>
              {groupedPlaces.mustgo.map(renderPlaceRow)}
            </Box>
          )}

          {/* Luxury */}
          {groupedPlaces.luxury.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'warning.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <RestaurantIcon fontSize="small" /> Luxury ({groupedPlaces.luxury.length})
              </Typography>
              {groupedPlaces.luxury.map(renderPlaceRow)}
            </Box>
          )}

          {/* Healthy */}
          {groupedPlaces.healthy.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'success.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <RestaurantIcon fontSize="small" /> Healthy ({groupedPlaces.healthy.length})
              </Typography>
              {groupedPlaces.healthy.map(renderPlaceRow)}
            </Box>
          )}

          {/* Others */}
          {groupedPlaces.others.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <RestaurantIcon fontSize="small" /> Others ({groupedPlaces.others.length})
              </Typography>
              {groupedPlaces.others.map(renderPlaceRow)}
            </Box>
          )}

          {/* Visited */}
          {groupedPlaces.visited.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'success.main', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <CheckCircleIcon fontSize="small" /> Visited ({groupedPlaces.visited.length})
              </Typography>
              {groupedPlaces.visited.map(renderPlaceRow)}
            </Box>
          )}
        </>
      )}

      {/* Map view with list below */}
      {viewMode === 'map' && (
        <Box>
          {places.map(renderPlaceRow)}
        </Box>
      )}

      {places.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No places yet. Start adding your favorite food spots!
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle>{editingPlace ? 'Edit Place' : 'Add New Place'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {!mapsReady && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Loading Google Places... Please wait a moment.
                </Typography>
              </Box>
            )}
            <Autocomplete
              freeSolo
              options={predictions}
              inputValue={searchQuery}
              onInputChange={handleSearchChange}
              onChange={handleSelectPrediction}
              disabled={!mapsReady}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={mapsReady ? "Search for a place" : "Loading..."}
                  fullWidth
                  margin="dense"
                  required
                />
              )}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="mustgo">Must Go</MenuItem>
                <MenuItem value="luxury">Luxury</MenuItem>
                <MenuItem value="healthy">Healthy</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Remarks"
              fullWidth
              margin="dense"
              multiline
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />

            {placeDetails && (
              <Box sx={{
                mt: 2,
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                {placeDetails.photoURL && (
                  <Avatar
                    src={placeDetails.photoURL}
                    variant="rounded"
                    sx={{ width: 56, height: 56 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                    {placeDetails.name}
                  </Typography>
                  {placeDetails.stars && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <StarIcon sx={{ fontSize: 14, color: '#ffc107' }} />
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {placeDetails.stars}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!placeDetails}>
            {editingPlace ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          const place = places.find(p => p.id === selectedPlaceId);
          handleOpenDialog(place);
          handleCloseMenu();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedPlaceId)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FoodList;
